/**
 * Step 6, Create a test order (Phase 65 / BLU-594 PR4; layout B, BLU-1376).
 * A trimmed card (NOT the live-search TestOrderModal): pick a location, use
 * the first synced product (or type a SKU), and create a synthetic is_test
 * order that flows through the production stations without touching Shopify
 * or shipping. Complete once an order is created (the page writes the server
 * flag through `onDone`), or when the operator marks it done.
 *
 * The create button is the pane's footer primary, so the form state lives in
 * the active component. A created order's success panel is the pane's saved
 * summary, so it stays visible once the step reads complete (handover 2,
 * "Success disappears"); until the flag lands the body shows it instead.
 *
 * A locked step renders the frame only: the active component (locations,
 * product preview and the mutation) never mounts, so its queries stay lazy.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, FlaskConical } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useSavedHold } from '../../hooks/useSavedHold';
import { StepPane } from '../shell/StepPane';
import { useStepChrome } from '../shell/StepChrome';
import { SavePrimaryButton } from '../shell/SavePrimaryButton';

interface Step6Props {
  onDone: () => void;
}

export function Step6TestOrder({ onDone }: Step6Props) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();
  if (state === 'locked') {
    return <StepPane title={t('step6.title')} description={t('step6.description')} />;
  }
  return <Step6Active onDone={onDone} />;
}

function Step6Active({ onDone }: Step6Props) {
  const { t } = useTranslation('onboarding');
  const { state } = useStepChrome();
  const { locations } = useLocations();
  // Only a default product to offer: no need to rescan on every focus.
  const previewQuery = trpc.productSync.getProductSyncPreview.useQuery(
    { pageSize: 5 },
    { staleTime: 60_000, refetchOnWindowFocus: false },
  );

  const [locationId, setLocationId] = useState('');
  const [manualSku, setManualSku] = useState('');
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  // "Saved" holds on the button for a moment after a create (DESIGN 5).
  const savedHold = useSavedHold();

  // First synced product with a derivable SKU (informational default source).
  const firstProduct = previewQuery.data?.products.find((p) => p.variants.some((v) => v.derived.ok));
  const firstOkVariant = firstProduct?.variants.find((v) => v.derived.ok);
  const firstSku = firstOkVariant && firstOkVariant.derived.ok ? firstOkVariant.derived.sku : null;

  const createTestOrder = trpc.lineItemExclusions.createTestOrder.useMutation({
    onSuccess: (res) => {
      setCreatedNumber(res.order_name?.replace(/^#/, '') ?? String(res.shopify_order_id));
      savedHold.markSaved();
      onDone();
    },
    onError: (e) => toast.error(t('step6.error', { message: e.message })),
  });

  const effectiveSku = manualSku.trim() || firstSku;
  const canSubmit = !!locationId && !!effectiveSku && !createTestOrder.isPending;

  const handleCreate = () => {
    if (!locationId || !effectiveSku) return;
    const useManual = !!manualSku.trim();
    createTestOrder.mutate({
      location_id: locationId,
      items: [
        useManual || !firstProduct
          ? { sku: effectiveSku, product_name: 'Onboarding test order', quantity: 1, price: 25, product_tags: [], is_gift_card: false }
          : {
              sku: firstSku!,
              product_name: firstProduct.title,
              shopify_product_id: firstProduct.id,
              shopify_variant_id: firstOkVariant!.id,
              quantity: 1,
              price: 25,
              product_tags: [],
              is_gift_card: false,
            },
      ],
    });
  };

  const successPanel = createdNumber ? (
    <div>
      <p className="font-semibold">{t('step6.successTitle')}</p>
      <p className="mt-0.5">{t('step6.successBody')}</p>
      <p className="mt-1 font-mono text-xs">{t('step6.orderNumber', { number: createdNumber })}</p>
    </div>
  ) : null;

  const settled = state === 'done';
  const phase = savedHold.phase(createTestOrder.isPending);

  return (
    <StepPane
      title={t('step6.title')}
      description={t('step6.description')}
      summary={successPanel ?? t('step6.doneNote')}
      primary={
        <SavePrimaryButton phase={phase} disabled={!canSubmit} onClick={handleCreate}>
          <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
          {t('step6.create')}
        </SavePrimaryButton>
      }
    >
      <div className="space-y-4">
        {successPanel && !settled && (
          <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
            {successPanel}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="onboarding-test-location">{t('step6.location')}</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger id="onboarding-test-location" className="h-9">
                <SelectValue placeholder={t('step6.locationPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>{t('step6.product')}</Label>
            {firstSku ? (
              <div className="flex h-9 items-center gap-2 rounded-md border bg-muted/40 px-3">
                <FlaskConical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="truncate text-xs" title={firstProduct?.title}>{firstProduct?.title}</span>
                <span className="ml-auto shrink-0 font-mono text-[11px] text-muted-foreground">{firstSku}</span>
              </div>
            ) : (
              <p className="flex h-9 items-center text-xs text-muted-foreground">{t('step6.noProduct')}</p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="onboarding-test-sku">{t('step6.manualSku')}</Label>
          <Input
            id="onboarding-test-sku"
            value={manualSku}
            onChange={(e) => setManualSku(e.target.value)}
            placeholder={t('step6.manualSkuPlaceholder')}
            className="h-9 max-w-xs font-mono text-xs"
          />
        </div>
      </div>
    </StepPane>
  );
}
